import React, { FC, useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  CircularProgress,
  Chip,
  Input,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Send,
  AttachFile,
  InsertDriveFile,
  Image as ImageIcon,
  ArrowBack,
} from '@mui/icons-material';
import { useMessagingContext } from '../../../contexts/messaging.context';
import isAppWithMock from 'src/utils/isAppWithMock';
import {
  MessageWithRelations,
  MessageType,
  ConversationSummary,
  BroadcastMessagePayload,
} from '../../../../../../apis/messaging/messaging.interfaces';
import { localStorageGet } from '../../../../../../utils/localStorage';
import { ILoggedInUser } from '../../../../../../apis/auth/auth.interfaces';
import { format } from 'date-fns';
import { useTranslation } from 'src/hooks/useTranslationHook';

type ChatWindowProps = {
  messagingMobileHistoryKey?: string;
};

const ChatWindow: FC<ChatWindowProps> = ({
  messagingMobileHistoryKey = 'messagingMobileChat',
}) => {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const { translateText } = useTranslation();
  const {
    state,
    sendDirectMessage,
    sendBroadcastMessage,
    setSelectedReceiver,
    setSelectedClass,
    setSelectedConversation,
    setPendingNewConversation,
    sendTypingIndicator,
    sendStopTypingIndicator,
    markMessagesAsRead,
  } = useMessagingContext();

  const [messageText, setMessageText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesScrollContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMarkedReadRef = useRef<string>('');
  const markAsReadMutationRef = useRef(markMessagesAsRead);

  // Keep mutation ref updated
  useEffect(() => {
    markAsReadMutationRef.current = markMessagesAsRead;
  }, [markMessagesAsRead]);

  // Check if mock mode is enabled
  const withMockData = isAppWithMock();

  // Get current user from JWT token or use mock user
  const currentUser: ILoggedInUser = withMockData
    ? ({
      id: 'mock_user',
      email: 'mock@example.com',
      firstName: 'Mock',
      lastName: 'User',
      roles: [],
      token: '',
      type: 'user',
      username: 'mock_user',
      tokenType: 'Bearer',
    } as ILoggedInUser)
    : (() => {
      const token = localStorageGet('token', '') as string;
      if (token) {
        try {
          const payload = token.split('.')[1];
          if (payload) {
            const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
              payload.length + ((4 - (payload.length % 4)) % 4), '='
            );
            const decoded = JSON.parse(window.atob(padded));
            return {
              id: decoded._id || decoded.id || '',
              email: decoded.email || '',
              firstName: decoded.first_name || '',
              lastName: decoded.last_name || '',
              roles: decoded.roles || [],
              token,
              type: decoded.user_role || 'user',
              username: decoded.username || decoded.email || '',
              tokenType: 'Bearer',
            } as ILoggedInUser;
          }
        } catch { /* ignore */ }
      }
      return {} as ILoggedInUser;
    })();

  // Determine which messages to show and sort by date (oldest first)
  const currentMessages = React.useMemo(() => {
    const messages = state.selectedReceiverId
      ? state.directMessages
      : state.selectedClassId
        ? state.broadcastMessages
        : [];

    // Sort messages by created_at (oldest to newest) for proper display
    return [...messages].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateA - dateB;
    });
  }, [
    state.selectedReceiverId,
    state.selectedClassId,
    state.directMessages,
    state.broadcastMessages,
  ]);

  const currentConversationId =
    state.selectedReceiverId || state.selectedClassId;

  // Scroll only the message pane — scrollIntoView also scrolls the window and causes a layout jump
  useEffect(() => {
    const el = messagesScrollContainerRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [currentMessages]);

  // Mark messages as read when conversation changes (debounced to avoid loops)
  useEffect(() => {
    if (!currentConversationId || !currentUser?.id) {
      lastMarkedReadRef.current = '';
      return;
    }

    const conversationKey = `${currentConversationId}-${currentUser.id}`;

    // Skip if we've already marked this conversation as read
    if (lastMarkedReadRef.current === conversationKey) {
      return;
    }

    // Use a timeout to debounce and avoid immediate re-runs
    const timeoutId = setTimeout(() => {
      // Get current messages for this conversation
      const messages = state.selectedReceiverId
        ? state.directMessages
        : state.selectedClassId
          ? state.broadcastMessages
          : [];

      if (messages.length > 0) {
        const unreadMessageIds = messages
          .filter(
            (msg: MessageWithRelations) =>
              !msg.is_read && msg.sender_id !== currentUser.id
          )
          .map((msg: MessageWithRelations) => msg._id || msg.id)
          .filter((id): id is string => !!id);

        if (unreadMessageIds.length > 0) {
          lastMarkedReadRef.current = conversationKey;
          markAsReadMutationRef.current.mutate(unreadMessageIds);
        } else {
          // Mark as processed even if no unread messages
          lastMarkedReadRef.current = conversationKey;
        }
      }
    }, 100);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, currentUser?.id]);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        const newFiles = Array.from(files);
        setAttachedFiles((prev) => [...prev, ...newFiles]);
      }
      // Reset input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    []
  );

  const handleRemoveFile = useCallback((index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon fontSize="small" />;
    }
    return <InsertDriveFile fontSize="small" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return translateText('communications.messaging.zeroBytes');
    const k = 1024;
    const sizes = [
      translateText('communications.messaging.bytes'),
      translateText('communications.messaging.kb'),
      translateText('communications.messaging.mb'),
      translateText('communications.messaging.gb'),
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleSendMessage = useCallback(async () => {
    // Prevent multiple simultaneous sends
    if (state.isSendingMessage) {
      return;
    }

    const userId = currentUser?.id || (currentUser as any)?._id;
    const selectedReceiverId = state.selectedReceiverId;
    const selectedClassId = state.selectedClassId;

    // Validation
    if ((!messageText.trim() && attachedFiles.length === 0) || !userId) {
      return;
    }

    // Get institute_ref from various sources (read from state directly, not as dependency)
    let instituteRef = '';

    // Try from current messages first
    const messages = selectedReceiverId
      ? state.directMessages
      : selectedClassId
        ? state.broadcastMessages
        : [];
    if (messages.length > 0) {
      instituteRef = messages[0]?.institute_ref || '';
    }

    // Fallback to conversations
    if (!instituteRef && state.conversations.length > 0) {
      instituteRef = state.conversations[0]?.last_message?.institute_ref || '';
    }

    // Fallback to any conversation's last message
    if (!instituteRef) {
      const conversationWithRef = state.conversations.find(
        (c) => c.last_message?.institute_ref
      );
      instituteRef = conversationWithRef?.last_message?.institute_ref || '';
    }

    // Fallback to JWT token's institute_ref
    if (!instituteRef) {
      try {
        const token = localStorageGet('token', '') as string;
        if (token) {
          const payload = token.split('.')[1];
          if (payload) {
            const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
              payload.length + ((4 - (payload.length % 4)) % 4), '='
            );
            const decoded = JSON.parse(window.atob(padded));
            instituteRef = decoded.institute_ref || '';
          }
        }
      } catch { /* ignore */ }
    }

    // Build message content - include file names if files are attached
    let content = messageText.trim();
    if (attachedFiles.length > 0) {
      const fileNames = attachedFiles.map((f) => f.name).join(', ');
      const filesLabel = translateText('communications.messaging.files');
      content = content
        ? `${content}\n[${filesLabel}: ${fileNames}]`
        : `[${filesLabel}: ${fileNames}]`;
    }

    try {
      if (selectedReceiverId) {
        const payload = {
          content,
          receiver_id: selectedReceiverId,
          type: MessageType.DIRECT_MESSAGE,
          institute_ref: instituteRef,
          sender_id: userId,
        };
        await sendDirectMessage.mutateAsync(payload);
      } else if (selectedClassId) {
        const payload: BroadcastMessagePayload & { sender_id?: string } = {
          content,
          class_id: selectedClassId,
          type: MessageType.BROADCAST,
          institute_ref: instituteRef,
          sender_id: userId,
        };
        await sendBroadcastMessage.mutateAsync(payload);
      } else {
        console.warn('No receiver or class selected');
        return;
      }

      // Clear form after successful send
      setMessageText('');
      setAttachedFiles([]);
      setIsTyping(false);
      setSendError(null);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage =
        (error as Error)?.message ||
        translateText('communications.messaging.failedToSend');
      setSendError(errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    messageText,
    attachedFiles,
    state.selectedReceiverId,
    state.selectedClassId,
    state.isSendingMessage,
    currentUser?.id,
    sendDirectMessage,
    sendBroadcastMessage,
  ]);

  const handleTyping = useCallback(() => {
    const userId = currentUser?.id;
    if (!currentConversationId || !userId) return;

    if (!isTyping) {
      setIsTyping(true);
      sendTypingIndicator({
        userId: userId,
        conversationId: currentConversationId,
        isTyping: true,
      });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      if (currentConversationId && userId) {
        sendStopTypingIndicator({
          userId: userId,
          conversationId: currentConversationId,
          isTyping: false,
        });
      }
    }, 1000);
  }, [
    isTyping,
    currentConversationId,
    currentUser?.id,
    sendTypingIndicator,
    sendStopTypingIndicator,
  ]);

  const getConversationName = (): string => {
    if (state.selectedReceiverId) {
      const conversation = state.conversations.find(
        (c: ConversationSummary) =>
          c.other_user?._id === state.selectedReceiverId
      );
      if (conversation?.other_user) {
        return `${conversation.other_user.first_name} ${conversation.other_user.last_name}`;
      }
      if (
        state.pendingNewConversation?._id === state.selectedReceiverId
      ) {
        return `${state.pendingNewConversation.first_name} ${state.pendingNewConversation.last_name}`;
      }
    } else if (state.selectedClassId) {
      const conversation = state.conversations.find(
        (c: ConversationSummary) => c.class_info?._id === state.selectedClassId
      );
      if (conversation?.class_info) {
        return `${conversation.class_info.level_name}${conversation.section ? ` - ${conversation.section}` : ''}`;
      }
    }
    return translateText('communications.messaging.selectConversationTitle');
  };

  const getConversationAvatar = (): string => {
    if (state.selectedReceiverId) {
      const conversation = state.conversations.find(
        (c: ConversationSummary) =>
          c.other_user?._id === state.selectedReceiverId
      );
      if (conversation?.other_user?.profile_picture) {
        return conversation.other_user.profile_picture;
      }
      if (state.pendingNewConversation?._id === state.selectedReceiverId) {
        return state.pendingNewConversation.profile_picture || '';
      }
    }
    return '';
  };

  const isMyMessage = (message: MessageWithRelations): boolean => {
    if (!currentUser?.id || !message.sender_id) {
      return false;
    }
    // Convert both to strings for reliable comparison
    const senderId = String(message.sender_id);
    const userId = String(currentUser.id);
    const isMine = senderId === userId;

    return isMine;
  };

  const formatMessageTime = (date: string | Date): string => {
    try {
      return format(new Date(date), 'HH:mm');
    } catch {
      return '';
    }
  };

  const clearConversationSelection = useCallback(() => {
    setSelectedReceiver(null);
    setSelectedClass(null);
    setSelectedConversation(null);
    setPendingNewConversation(null);
  }, [
    setSelectedReceiver,
    setSelectedClass,
    setSelectedConversation,
    setPendingNewConversation,
  ]);

  const handleHeaderBack = useCallback(() => {
    const st = window.history.state as Record<string, unknown> | null;
    if (st?.[messagingMobileHistoryKey]) {
      window.history.back();
    } else {
      clearConversationSelection();
    }
  }, [messagingMobileHistoryKey, clearConversationSelection]);

  const formatMessageDate = (date: string | Date): string => {
    try {
      const messageDate = new Date(date);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (messageDate.toDateString() === today.toDateString()) {
        return translateText('communications.messaging.today');
      } else if (messageDate.toDateString() === yesterday.toDateString()) {
        return translateText('communications.messaging.yesterday');
      } else {
        return format(messageDate, 'MMM dd, yyyy');
      }
    } catch {
      return '';
    }
  };

  const shouldShowDateSeparator = (currentIndex: number): boolean => {
    if (currentIndex === 0) return true;
    const currentMsg = currentMessages[currentIndex];
    const prevMsg = currentMessages[currentIndex - 1];
    if (!currentMsg || !prevMsg) return false;
    const currentDate = new Date(currentMsg.created_at).toDateString();
    const prevDate = new Date(prevMsg.created_at).toDateString();
    return currentDate !== prevDate;
  };

  if (!state.selectedReceiverId && !state.selectedClassId) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {translateText('communications.messaging.selectConversation')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      data-testid="chat-window"
    >
      {/* Chat Header */}
      <Paper
        elevation={1}
        sx={{
          p: { xs: 1, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          borderRadius: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {isMobileLayout && (
          <IconButton
            size="small"
            onClick={handleHeaderBack}
            sx={{
              mr: { xs: 0, sm: -1 },
              minHeight: { xs: '44px', sm: 'auto' },
              minWidth: { xs: '44px', sm: 'auto' },
            }}
            aria-label={translateText(
              'communications.messaging.backToChats'
            )}
            data-testid="messaging-chat-back-btn"
          >
            <ArrowBack />
          </IconButton>
        )}
        <Avatar src={getConversationAvatar()}>
          {getConversationName().charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {getConversationName()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="caption"
              color={state.isConnected ? 'success.main' : 'text.secondary'}
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              {state.isConnected
                ? translateText('communications.messaging.online')
                : translateText('communications.messaging.offline')}
            </Typography>
            {state.connectionError && (
              <Typography
                variant="caption"
                color="error.main"
                sx={{
                  ml: 1,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                }}
              >
                • {translateText('communications.messaging.connectionError')}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Connection Error Alert */}
      {state.connectionError && (
        <Alert
          severity="warning"
          onClose={() => { }}
          sx={{ m: 1, borderRadius: 0 }}
        >
          {translateText('communications.messaging.connectionIssue')}:{' '}
          {state.connectionError}
        </Alert>
      )}

      {/* General Error Alert */}
      {state.error && !state.connectionError && (
        <Alert
          severity="error"
          onClose={() => { }}
          sx={{ m: 1, borderRadius: 0 }}
        >
          {state.error}
        </Alert>
      )}

      {/* Messages List */}
      <Box
        ref={messagesScrollContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'background.default',
          p: { xs: 1, sm: 2 },
        }}
      >
        {state.isLoadingMessages ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : currentMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {translateText('communications.messaging.noMessages')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {currentMessages.map(
              (message: MessageWithRelations, index: number) => {
                const showDateSeparator = shouldShowDateSeparator(index);
                const myMessage = isMyMessage(message);

                return (
                  <React.Fragment key={message._id}>
                    {showDateSeparator && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          my: 2,
                        }}
                      >
                        <Chip
                          label={formatMessageDate(message.created_at)}
                          size="small"
                          sx={{
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </Box>
                    )}
                    <ListItem
                      sx={{
                        display: 'flex',
                        justifyContent: myMessage ? 'flex-end' : 'flex-start',
                        px: 0,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '70%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: myMessage ? 'flex-end' : 'flex-start',
                          gap: 0.5,
                        }}
                      >
                        {!myMessage && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ px: 1 }}
                          >
                            {message.sender?.first_name}{' '}
                            {message.sender?.last_name}
                          </Typography>
                        )}
                        <Paper
                          elevation={myMessage ? 1 : 0}
                          sx={{
                            p: { xs: 1, sm: 1.5 },
                            bgcolor: myMessage
                              ? 'primary.main'
                              : 'background.paper',
                            color: myMessage ? 'white' : 'text.primary',
                            borderRadius: 1.5,
                            wordBreak: 'break-word',
                            border: myMessage ? 'none' : '1px solid',
                            borderColor: myMessage ? 'transparent' : 'divider',
                            minWidth: { xs: '80px', sm: '120px' },
                            maxWidth: { xs: '85%', sm: '70%' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                          >
                            {message.content}
                          </Typography>
                        </Paper>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            px: 1,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          }}
                        >
                          {formatMessageTime(message.created_at)}
                          {message.is_read && myMessage && ' ✓✓'}
                        </Typography>
                      </Box>
                    </ListItem>
                  </React.Fragment>
                );
              }
            )}
            {/* Typing Indicator */}
            {currentConversationId &&
              state.typingUsers.has(currentConversationId) && (
                <ListItem sx={{ justifyContent: 'flex-start', px: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: 'background.paper',
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {translateText('communications.messaging.typing')}
                    </Typography>
                  </Paper>
                </ListItem>
              )}
          </List>
        )}
      </Box>

      {/* Message Input */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1, sm: 2 },
          borderRadius: 0,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {/* Attached Files Preview */}
        {attachedFiles.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1,
              p: 1,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {attachedFiles.map((file, index) => (
              <Chip
                key={index}
                icon={getFileIcon(file)}
                label={`${file.name} (${formatFileSize(file.size)})`}
                onDelete={() => handleRemoveFile(index)}
                size="small"
                sx={{ maxWidth: '200px' }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'flex-end' }}>
          <IconButton
            size="small"
            color="primary"
            component="label"
            disabled={state.isSendingMessage}
            sx={{
              minHeight: { xs: '44px', sm: 'auto' },
              minWidth: { xs: '44px', sm: 'auto' },
            }}
          >
            <AttachFile />
            <Input
              ref={fileInputRef}
              type="file"
              inputProps={{ multiple: true }}
              onChange={handleFileSelect}
              sx={{ display: 'none' }}
            />
          </IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={translateText('communications.messaging.typeMessage')}
            value={messageText}
            onChange={(e) => {
              setMessageText(e.target.value);
              handleTyping();
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={state.isSendingMessage}
            size="small"
            sx={{
              '& .MuiInputBase-input': {
                fontSize: { xs: '16px', sm: '14px' },
                minHeight: { xs: '44px', sm: 'auto' },
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={
              (!messageText.trim() && attachedFiles.length === 0) ||
              state.isSendingMessage
            }
            sx={{
              minHeight: { xs: '44px', sm: 'auto' },
              minWidth: { xs: '44px', sm: 'auto' },
            }}
          >
            {state.isSendingMessage ? <CircularProgress size={20} /> : <Send />}
          </IconButton>
        </Box>
      </Paper>

      {/* Send Error Snackbar */}
      <Snackbar
        open={!!sendError}
        autoHideDuration={6000}
        onClose={() => setSendError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSendError(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {sendError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatWindow;
